import {
  Box,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  type BoxRegion,
  type ManifestFormVariant,
  boxRegion,
  knownBoxes,
  paddedRegion,
} from "components/Document/ReviewInteraction";
import {
  DocumentPreview,
  type DocumentPreviewHighlight,
} from "components/Document/shared/DocumentPreview";
import { safeParseInt } from "encamp-shared/src/tierii/utilities";
import { useEffect, useMemo, useState } from "react";

/**
 * Every box `formRegions` knows, drawn over a real manifest at once.
 *
 * The coordinates in `formRegions.ts` are hand-estimated from the printed layout
 * of EPA Form 8700-22, and nothing had ever checked them against a document. One
 * at a time against live manifests is the slow way to do that and gives no sense
 * of whether the map is coherent — whether the table rows line up with each
 * other, whether the bands tile without overlapping. Drawn together, a box in the
 * wrong place is obvious.
 *
 * Staff-only and deliberately unglamorous: this is a measuring tool, not a
 * feature. Load a manifest, compare, and correct `formRegions.ts` — which is a
 * code change reviewed like any other, because the form's layout only changes
 * when EPA revises the form.
 *
 * It draws the padded regions too, on a toggle, because that is what the
 * follow-the-caret zoom actually frames. A box that looks right and a padding
 * that runs off the sheet are different problems.
 */

const VARIANTS: ManifestFormVariant[] = ["8700-22", "8700-22A"];

/** Narrows the select's string back to a variant, rather than asserting it is one. */
function isVariant(value: string): value is ManifestFormVariant {
  return VARIANTS.some((variant) => variant === value);
}

/** How many rows the table on each sheet has, for the row selector's range. */
const TABLE_ROWS: Record<ManifestFormVariant, number> = { "8700-22": 4, "8700-22A": 8 };

interface SheetBox {
  box: string;
  region: BoxRegion;
}

function sheetBoxes(variant: ManifestFormVariant, rowIndex: number): SheetBox[] {
  return knownBoxes(variant)
    .map((box) => ({ box, region: boxRegion(variant, box, rowIndex) }))
    .flatMap((entry) => (entry.region == null ? [] : [{ box: entry.box, region: entry.region }]));
}

export function FormRegionSheet() {
  const [variant, setVariant] = useState<ManifestFormVariant>("8700-22");
  const [rowIndex, setRowIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [showPadding, setShowPadding] = useState(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  // Object URLs outlive the component unless revoked, and a verifier loads a
  // handful of manifests in a sitting.
  useEffect(() => {
    if (fileUrl == null) return;
    return () => URL.revokeObjectURL(fileUrl);
  }, [fileUrl]);

  const boxes = useMemo(() => sheetBoxes(variant, rowIndex), [variant, rowIndex]);

  const highlights: DocumentPreviewHighlight[] = useMemo(
    () =>
      boxes.map(({ box, region }) => ({
        page,
        region: showPadding ? paddedRegion(region) : region,
        label: box,
      })),
    [boxes, page, showPadding]
  );

  return (
    <Stack spacing={2} sx={{ height: "100%", minHeight: 0 }}>
      <Typography variant="body2" color="text.secondary">
        Every box <code>formRegions.ts</code> knows, drawn over a manifest you supply. The
        coordinates are hand-estimated; correct them in <code>formRegions.ts</code>. Nothing here
        reads or writes any record.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Sheet"
          value={variant}
          onChange={(event) => {
            if (isVariant(event.target.value)) setVariant(event.target.value);
          }}
          sx={{ minWidth: 140 }}
        >
          {VARIANTS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Table row"
          value={rowIndex}
          onChange={(event) => setRowIndex(safeParseInt(event.target.value) ?? 0)}
          helperText="Which line the table boxes point at"
          sx={{ minWidth: 160 }}
        >
          {Array.from({ length: TABLE_ROWS[variant] }, (_, index) => (
            <MenuItem key={index} value={index}>
              Row {index + 1}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          type="number"
          label="Page"
          value={page}
          onChange={(event) => setPage(Math.max(1, safeParseInt(event.target.value) ?? 1))}
          helperText="Page of your file the sheet is on"
          sx={{ width: 130 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={showPadding}
              onChange={(event) => setShowPadding(event.target.checked)}
            />
          }
          label="Show padding"
        />

        <Box component="label" sx={{ display: "inline-flex", alignItems: "center" }}>
          <input
            type="file"
            accept="application/pdf"
            onChange={(event) => {
              const file = event.target.files?.[0];
              setFileUrl(file == null ? null : URL.createObjectURL(file));
            }}
          />
        </Box>

        <Typography variant="caption" color="text.secondary">
          {boxes.length} boxes
        </Typography>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 400 }}>
        {fileUrl == null ? (
          <Paper
            variant="outlined"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">Choose a manifest PDF to draw the regions over.</Typography>
          </Paper>
        ) : (
          <DocumentPreview
            fileUrl={fileUrl}
            highlights={highlights}
            // Nothing to frame: the whole point is seeing the map at once, and a
            // zoom onto one box would defeat that.
            frame={null}
          />
        )}
      </Box>
    </Stack>
  );
}
